"use client";

// The language of a free test page for its client components (2026-11-04).
// The server picks the messages (lib/i18n/messages.ts: only the namespaces the
// page's test needs) and passes them here; components call useT(). The
// language also goes into the test analytics and every scored run
// (lib/freeTests.ts setTestLocale), so results can be grouped by language.
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { createTranslator, type Messages, type Translator } from "@/lib/i18n/translate";
import { setTestLocale } from "@/lib/freeTests";

const I18nContext = createContext<Translator | null>(null);

export function I18nProvider({ locale, messages, children }: { locale: string; messages: Messages; children: ReactNode }) {
  const value = useMemo(() => createTranslator(locale, messages), [locale, messages]);
  // Set during render (not in an effect) so the first analytics event of the page already has it.
  setTestLocale(locale);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** The page's translator. */
export function useT(): Translator {
  const t = useContext(I18nContext);
  if (!t) throw new Error("useT() outside <I18nProvider>");
  return t;
}
